import {
    formatRelativeTime,
    parseUTCDate,
} from "@mrflark/core/utils";
import type {
    ShareLink,
} from "@mrflark/spl_service_model";
import {
    Button,
    LoadingSpinner,
    Modal,
} from "@mrflark/ui";
import {
    Check,
    Clock,
    Copy,
    Link,
    Trash2,
} from "lucide-preact";
import {
    useCallback,
    useEffect,
    useState,
} from "preact/compat";
import {
    toast,
} from "react-hot-toast";
import {
    splClient,
} from "../../api/client.ts";

const EXPIRATION_OPTIONS = [
    {
        label: "1 hour",
        value: 1,
    },
    {
        label: "24 hours",
        value: 24,
    },
    {
        label: "7 days",
        value: 168,
    },
    {
        label: "30 days",
        value: 720,
    },
    {
        label: "Never",
        value: 0,
    },
] as const;

type ShareModalProps = {
    isOpen: boolean;
    onClose: () => void;
    mediaId: string | null;
};

export const ShareModal = ({
    isOpen,
    onClose,
    mediaId,
}: ShareModalProps) => {
    const [shares, setShares] = useState<ShareLink[]>([]);
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [expirationHours, setExpirationHours] = useState(168);
    const [copiedId, setCopiedId] = useState<string | null>(null);

    const fetchShares = useCallback(async () => {
        if (!mediaId) return;
        setLoading(true);
        try {
            const response = await splClient.getMediaShares(mediaId);
            setShares(response.shares);
        } catch {
            toast.error("Failed to load share links");
        } finally {
            setLoading(false);
        }
    }, [mediaId]);

    useEffect(() => {
        if (isOpen && mediaId) {
            fetchShares();
        }
        if (!isOpen) {
            setShares([]);
            setCopiedId(null);
        }
    }, [isOpen, mediaId, fetchShares]);

    const handleCreate = async () => {
        if (!mediaId) return;
        setCreating(true);
        try {
            const options = expirationHours > 0
                ? {
                    expiresInHours: expirationHours,
                }
                : {};
            const newShare = await splClient.createShareLink(mediaId, options);
            setShares((prev) => [newShare, ...prev]);
            toast.success("Share link created");
        } catch {
            toast.error("Failed to create share link");
        } finally {
            setCreating(false);
        }
    };

    const handleRevoke = async (shareId: string) => {
        try {
            await splClient.revokeShareLink(shareId);
            setShares((prev) => prev.filter((share) => share.id !== shareId));
            toast.success("Share link revoked");
        } catch {
            toast.error("Failed to revoke share link");
        }
    };

    const handleCopy = async (shareUrl: string, shareId: string) => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopiedId(shareId);
            toast.success("Link copied to clipboard");
            setTimeout(() => setCopiedId(null), 2000);
        } catch {
            toast.error("Failed to copy link");
        }
    };

    const getExpiryStatus = (
        share: ShareLink,
    ): { label: string; className: string; } => {
        if (!share.expiresAt) {
            return {
                label: "Never expires",
                className: "text-text-muted",
            };
        }
        const expiresAt = parseUTCDate(share.expiresAt);
        if (expiresAt < new Date()) {
            return {
                label: "Expired",
                className: "text-error",
            };
        }
        return {
            label: `Expires ${formatRelativeTime(share.expiresAt)}`,
            className: "text-text-secondary",
        };
    };

    const truncateUrl = (url: string, maxLength: number = 50): string => {
        if (url.length <= maxLength) return url;
        return `${url.slice(0, maxLength)}...`;
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Share Links"
            size="lg"
        >
            <div className="space-y-6">
                <div className="flex items-end gap-3">
                    <div className="flex-1">
                        <label
                            htmlFor="expiration-select"
                            className="block text-sm text-text-secondary mb-1"
                        >
                            Expiration
                        </label>
                        <select
                            id="expiration-select"
                            value={expirationHours}
                            onChange={(event) =>
                                setExpirationHours(
                                    Number(
                                        (event.target as HTMLInputElement)
                                            .value,
                                    ),
                                )}
                            className="w-full bg-bg-secondary border border-border rounded-md px-3 py-2 text-text-primary text-sm focus:outline-none focus:ring-1 focus:ring-accent"
                        >
                            {EXPIRATION_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <Button
                        onClick={handleCreate}
                        disabled={creating || !mediaId}
                        className="gap-1.5"
                    >
                        <Link size={16} />
                        {creating ? "Creating..." : "Create Share Link"}
                    </Button>
                </div>

                <div>
                    <h3 className="text-sm font-medium text-text-secondary mb-3">
                        Existing Links
                    </h3>

                    {loading
                        ? (
                            <div className="flex justify-center py-6">
                                <LoadingSpinner size="md" />
                            </div>
                        )
                        : shares.length === 0
                        ? (
                            <p className="text-text-muted text-sm text-center py-6">
                                No share links yet
                            </p>
                        )
                        : (
                            <div className="space-y-2">
                                {shares.map((share) => {
                                    const expiry = getExpiryStatus(share);
                                    return (
                                        <div
                                            key={share.id}
                                            className="flex items-center gap-3 p-3 bg-bg-secondary rounded-lg border border-border"
                                        >
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <span
                                                        className="text-sm font-mono text-text-primary truncate"
                                                        title={share.shareUrl}
                                                    >
                                                        {truncateUrl(
                                                            share.shareUrl,
                                                        )}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            handleCopy(
                                                                share.shareUrl,
                                                                share.id,
                                                            )}
                                                        className="flex-shrink-0 p-1 text-text-muted hover:text-text-primary rounded transition-colors"
                                                    >
                                                        {copiedId === share.id
                                                            ? (
                                                                <Check
                                                                    size={14}
                                                                    className="text-success"
                                                                />
                                                            )
                                                            : (
                                                                <Copy
                                                                    size={14}
                                                                />
                                                            )}
                                                    </button>
                                                </div>
                                                <div className="flex items-center gap-3 mt-1">
                                                    <span className="text-xs text-text-muted">
                                                        {share.accessCount}{" "}
                                                        {share.accessCount === 1
                                                            ? "view"
                                                            : "views"}
                                                    </span>
                                                    <span
                                                        className={`text-xs flex items-center gap-1 ${expiry.className}`}
                                                    >
                                                        <Clock size={12} />
                                                        {expiry.label}
                                                    </span>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    handleRevoke(share.id)}
                                                className="flex-shrink-0 p-1.5 text-text-muted hover:text-error rounded transition-colors"
                                                title="Revoke share link"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                </div>
            </div>
        </Modal>
    );
};
